import { type Address } from 'viem';
import { polygon, bsc, mainnet } from 'wagmi/chains';

/**
 * Bridge Architecture:
 *
 * Polygon: Uses OFT Adapter pattern
 *   - FAR Token (ERC20): 0x5F32AbeeBD3c2fac1E7459A27e1AE9f1C16ccccA
 *   - OFT Adapter: 0x1a53F62A8eeeE5CEa2aa822dD0c8B5ed1FF20159
 *   - Adapter wraps the existing ERC20 token for cross-chain functionality
 *
 * BSC & Ethereum: Use Native OFT pattern
 *   - FAR Token IS the OFT contract (LayerZero built-in)
 *   - No separate adapter needed
 *   - Token address = OFT address
 */

// LayerZero Endpoint IDs (EIDs)
export const ETH_EID = 30101 as const; // Ethereum Mainnet
export const BSC_EID = 30102 as const; // Binance Smart Chain
export const POL_EID = 30109 as const; // Polygon

// Network configuration for bridge
export interface BridgeNetworkConfig {
  chainId: number;
  eid: number;
  name: string;
  tokenAddress: Address;
  adapterAddress: Address;
}

// FAR Token addresses per chain (Production Mainnet)
// NOTE: BSC uses the new FAR token (0xC44F...) as the main token
//       Old FAR (0xDC816B...) is being deprecated and swapped to new FAR
export const FAR_TOKEN_ADDRESSES: Record<number, Address> = {
  [polygon.id]: '0x5F32AbeeBD3c2fac1E7459A27e1AE9f1C16ccccA', // Polygon FAR ERC20 (Mainnet)
  [bsc.id]: '0xDC816B6bd4786720e91Eb17D4DaBf0E1eEf5eE91', // BSC FAR (main token)
  [mainnet.id]: '0xf9Fa60EF4F23F00CCE403cc4d2c11Baf4880A0d6', // Ethereum FAR OFT (Mainnet) - Native OFT
} as const;

// LayerZero OFT/Adapter addresses per chain (Production Mainnet)
// Note: Polygon uses OFT Adapter (wraps existing ERC20)
//       BSC and Ethereum use native OFT (token = OFT contract)
export const ADAPTER_ADDRESSES: Record<number, Address> = {
  [polygon.id]: '0x1a53F62A8eeeE5CEa2aa822dD0c8B5ed1FF20159', // Polygon OFT Adapter (wraps FAR ERC20)
  [bsc.id]: '0xDC816B6bd4786720e91Eb17D4DaBf0E1eEf5eE91', // BSC FAR (main token)
  [mainnet.id]: '0xf9Fa60EF4F23F00CCE403cc4d2c11Baf4880A0d6', // Ethereum FAR OFT (same as token address)
} as const;

// Supported bridge networks configuration
export const BRIDGE_NETWORKS: BridgeNetworkConfig[] = [
  {
    chainId: polygon.id,
    eid: POL_EID,
    name: 'Polygon',
    tokenAddress: FAR_TOKEN_ADDRESSES[polygon.id],
    adapterAddress: ADAPTER_ADDRESSES[polygon.id],
  },
  {
    chainId: mainnet.id,
    eid: ETH_EID,
    name: 'Ethereum',
    tokenAddress: FAR_TOKEN_ADDRESSES[mainnet.id],
    adapterAddress: ADAPTER_ADDRESSES[mainnet.id],
  },
];

// Destination network (BSC - fixed for one-way bridge)
export const DESTINATION_NETWORK: BridgeNetworkConfig = {
  chainId: bsc.id,
  eid: BSC_EID,
  name: 'BSC',
  tokenAddress: FAR_TOKEN_ADDRESSES[bsc.id],
  adapterAddress: ADAPTER_ADDRESSES[bsc.id],
};

// ERC20 Token ABI (minimal - for approve, allowance, balanceOf, decimals)
export const TOKEN_ABI = [
  {
    inputs: [
      { internalType: 'address', name: 'spender', type: 'address' },
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'owner', type: 'address' },
      { internalType: 'address', name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// LayerZero OFT Adapter ABI (minimal - for quoteSend and send)
export const ADAPTER_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: 'uint32', name: 'dstEid', type: 'uint32' },
          { internalType: 'bytes32', name: 'to', type: 'bytes32' },
          { internalType: 'uint256', name: 'amountLD', type: 'uint256' },
          { internalType: 'uint256', name: 'minAmountLD', type: 'uint256' },
          { internalType: 'bytes', name: 'extraOptions', type: 'bytes' },
          { internalType: 'bytes', name: 'composeMsg', type: 'bytes' },
          { internalType: 'bytes', name: 'oftCmd', type: 'bytes' },
        ],
        internalType: 'struct SendParam',
        name: '_sendParam',
        type: 'tuple',
      },
      { internalType: 'bool', name: '_payInLzToken', type: 'bool' },
    ],
    name: 'quoteSend',
    outputs: [
      {
        components: [
          { internalType: 'uint256', name: 'nativeFee', type: 'uint256' },
          { internalType: 'uint256', name: 'lzTokenFee', type: 'uint256' },
        ],
        internalType: 'struct MessagingFee',
        name: 'msgFee',
        type: 'tuple',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'uint32', name: 'dstEid', type: 'uint32' },
          { internalType: 'bytes32', name: 'to', type: 'bytes32' },
          { internalType: 'uint256', name: 'amountLD', type: 'uint256' },
          { internalType: 'uint256', name: 'minAmountLD', type: 'uint256' },
          { internalType: 'bytes', name: 'extraOptions', type: 'bytes' },
          { internalType: 'bytes', name: 'composeMsg', type: 'bytes' },
          { internalType: 'bytes', name: 'oftCmd', type: 'bytes' },
        ],
        internalType: 'struct SendParam',
        name: '_sendParam',
        type: 'tuple',
      },
      {
        components: [
          { internalType: 'uint256', name: 'nativeFee', type: 'uint256' },
          { internalType: 'uint256', name: 'lzTokenFee', type: 'uint256' },
        ],
        internalType: 'struct MessagingFee',
        name: '_fee',
        type: 'tuple',
      },
      { internalType: 'address', name: '_refundAddress', type: 'address' },
    ],
    name: 'send',
    outputs: [
      {
        components: [
          { internalType: 'bytes32', name: 'guid', type: 'bytes32' },
          { internalType: 'uint64', name: 'nonce', type: 'uint64' },
          {
            components: [
              { internalType: 'uint256', name: 'nativeFee', type: 'uint256' },
              { internalType: 'uint256', name: 'lzTokenFee', type: 'uint256' },
            ],
            internalType: 'struct MessagingFee',
            name: 'fee',
            type: 'tuple',
          },
        ],
        internalType: 'struct MessagingReceipt',
        name: 'msgReceipt',
        type: 'tuple',
      },
      {
        components: [
          { internalType: 'uint256', name: 'amountSentLD', type: 'uint256' },
          {
            internalType: 'uint256',
            name: 'amountReceivedLD',
            type: 'uint256',
          },
        ],
        internalType: 'struct OFTReceipt',
        name: 'oftReceipt',
        type: 'tuple',
      },
    ],
    stateMutability: 'payable',
    type: 'function',
  },
] as const;

// Helper function to get network config by chain ID
export const getNetworkConfig = (chainId: number): BridgeNetworkConfig | undefined => {
  if (chainId === bsc.id) return DESTINATION_NETWORK;
  return BRIDGE_NETWORKS.find((network) => network.chainId === chainId);
};

// Helper function to get LayerZero EID by chain ID
export const getEidByChainId = (chainId: number): number | undefined => {
  return getNetworkConfig(chainId)?.eid;
};

// Empty bytes for LayerZero compose and OFT commands
export const EMPTY_BYTES = '0x' as const;

// LayerZero gas limit for destination chain execution
// This value should match the gas limit used in the old implementation
export const LZ_GAS_LIMIT = 200000 as const;
